import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Check, Mic, MicOff, Sparkles, Volume2, VolumeX } from "lucide-react";
import { useHandsFree, NOISE } from "../../components/coach/CoachCall";
import { unlockAudio } from "../../components/coach/CoachBits";
import { ExplainInput } from "../../components/soulumoCreate/ExplainInput";
import { MicHelp, MicCheck, useMicReady } from "../../components/coach/MicHelp";

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = '"Cormorant Garamond", Georgia, serif';
const C = { cream: "#F1ECDD", dim: "rgba(241,236,221,0.62)", gold: "#D7BB7A", bg: "var(--coach-bg, #0B1411)", line: "rgba(215,187,122,0.22)" };
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const OPEN = "Of course. What would you like your installation to help you with?";
const ADJUST = "No problem. What would you change or add?";

// Lets the member see they were heard while the guide works on a reply.
const Thinking = ({ text, testId = "create-help-busy" }) => (
  <div className="flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: "rgba(215,187,122,0.08)", border: `1px solid ${C.line}` }} data-testid={testId} aria-live="polite">
    <span className="flex gap-1" aria-hidden="true">
      {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full animate-bounce" style={{ background: C.gold, animationDelay: `${i * 0.15}s` }} />)}
    </span>
    <p className="text-sm" style={{ color: C.cream }} data-testid={`${testId}-text`}>{text}</p>
  </div>
);

const GuideWorking = ({ name, pronoun, kind }) => {
  const [t, setT] = useState(0);
  useEffect(() => { setT(0); const id = setInterval(() => setT((x) => x + 1), 1000); return () => clearInterval(id); }, [kind]);
  const text = kind === "write" ? (t < 8 ? `${name} is writing your affirmations…` : t < 18 ? `Shaping them around what you said…` : `Almost ready…`) : kind === "voice" ? `${name} is about to speak…`
    : t < 2 ? `${name} heard you.` : t < 6 ? `${name} is thinking…` : `${name} is preparing ${pronoun} reply…`;
  return <Thinking text={text} />;
};

// Mounted only while hands-free is on: listens after the guide finishes, sends what was said.
const CONFUSED = "I'm not quite catching you. Let's type it instead. I'm opening the text box for you.";
const HandsFree = ({ active, onText, onOff, onConfused }) => {
  const [note, setNote] = useState("");
  const [catching, setCatching] = useState(false);
  const misses = useRef(0);
  const miss = (msg) => { misses.current += 1; if (misses.current >= 2) onConfused(); else setNote(msg); };
  const mic = useHandsFree(active, async (blob) => {
    setNote(""); setCatching(true);
    try {
      const form = new FormData();
      form.append("file", blob, `speech.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
      const r = await axios.post(`${API}/api/audio-journeys/ai/transcribe`, form, auth());
      const t = (r.data?.text || "").trim();
      setCatching(false);
      if (t.length > 1 && !NOISE.has(t.toLowerCase().replace(/[^a-z ]/g, "").trim())) { misses.current = 0; return onText(t); }
      miss("I didn't quite catch that. Say it again?");
    } catch { setCatching(false); miss("I couldn't hear that clearly. Say it again?"); }
  }, { pauseTicks: 50, maxMs: 180000 });
  if (catching) return <Thinking text="Got it. Catching your words…" testId="create-help-catching" />;
  if (mic.error) return <MicHelp kind={mic.error} onRetry={mic.retry} onType={onOff} gold={C.gold} cream={C.cream} />;
  const label = (mic.manual ? (mic.manualRec ? "Listening… tap to finish" : "Tap to talk") : !active ? "" : mic.phase === "hearing" ? "I'm listening…" : "Speak whenever you're ready");
  return (
    <div className="flex items-center gap-3" data-testid="create-help-handsfree">
      {mic.manual ? (
        <button type="button" onClick={mic.tapToTalk} data-testid="create-help-tap-talk" className="w-11 h-11 rounded-full inline-flex items-center justify-center" style={{ background: mic.manualRec ? "#c0584f" : C.gold, color: C.bg }}><Mic size={18} /></button>
      ) : (
        <span className="w-11 h-11 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${C.gold}`, color: C.gold, transform: `scale(${1 + mic.level * 0.25})`, transition: "transform 120ms ease" }}><Mic size={18} /></span>
      )}
      <p className="flex-1 text-sm" style={{ color: C.gold }} data-testid="create-help-handsfree-status">{note || label}</p>
      <button type="button" onClick={onOff} data-testid="create-help-handsfree-off" className="text-xs inline-flex items-center gap-1" style={{ color: C.dim }}><MicOff size={13} /> Type instead</button>
    </div>
  );
};

// Soulumo's AI is used only here: talk it through → "Do these feel right?" → straight into Create.
export const CreateHelpChat = ({ who, name, play, speaking, onEnd }) => {
  const nav = useNavigate();
  const [turns, setTurns] = useState([{ role: "guide", text: OPEN }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(() => localStorage.getItem("create_help_muted") === "1");
  const [handsFree, setHandsFree] = useState(true);
  const [micReady, markMic] = useMicReady();
  const [resume, setResume] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/audio-journeys/flow-draft/chat`, auth())
      .then((r) => { const s = r.data.draft?.state; if (s?.turns?.length > 1) setResume(s); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (resume || (turns.length < 2 && !text.trim())) return undefined;
    const id = setTimeout(() => axios.put(`${API}/api/audio-journeys/flow-draft/chat`, { state: { turns, plan, text } }, auth()).catch(() => {}), 800);
    return () => clearTimeout(id);
  }, [turns, plan, text, resume]);
  const carryOn = () => { setTurns(resume.turns); setPlan(resume.plan || null); setText(resume.text || ""); setResume(null); };
  const startAgain = () => { axios.delete(`${API}/api/audio-journeys/flow-draft/chat`, auth()).catch(() => {}); setResume(null); };
  const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem("create_help_muted", m ? "1" : "0"); if (m) play(null); };
  const speakLine = async (line) => {
    if (muted) return;
    try { const r = await axios.post(`${API}/api/soulumo/create-chat/say`, { who, line }, auth()); if (r.data.audio_url) play(r.data.audio_url, r.data.env); } catch { /* text still shows */ }
  };
  const voiceReply = async (line) => {
    if (muted || !line) return;
    try { const v = await axios.post(`${API}/api/soulumo/create-chat/voice`, { who, text: line.slice(0, 600) }, auth()); if (v.data.audio_url) play(v.data.audio_url, v.data.env); } catch { /* text still shows */ }
  };
  useEffect(() => { speakLine("open"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Connection or reception trouble: hand over to the Create text box with everything said so far.
  const toTextBox = (said) => {
    const words = said.map((t) => t.trim()).filter(Boolean).join(" ");
    setError("I'm having trouble on my side, so I'm opening the text box with what you've said so far.");
    setTimeout(() => nav(`/my-journeys?explain=${encodeURIComponent(words)}`), 1800);
  };
  const userWords = (ts) => ts.filter((t) => t.role === "user").map((t) => t.text);
  const send = async (said) => {
    const next = [...turns, { role: "user", text: (said || text).trim() }];
    setTurns(next); setText(""); setBusy("reply"); setError("");
    try {
      const r = await axios.post(`${API}/api/soulumo/create-chat`, { who, turns: next.slice(-40), speak: false }, auth());
      setTurns([...next, { role: "guide", text: r.data.text }]);
      setBusy(r.data.ready ? "write" : "voice");
      const spoken = voiceReply(r.data.text);
      if (r.data.ready) {
        setBusy("write");
        if (r.data.plan) setPlan(r.data.plan);
        else setPlan((await axios.post(`${API}/api/audio-journeys/ai/ask`, { intent_text: r.data.summary }, auth())).data);
      }
      if (r.data.ready) setHandsFree(false);
      await spoken;
    } catch (e) {
      setHandsFree(false);
      if (e?.response?.status === 403) { setTurns(turns); setText(said || text); setError("Talking it through comes with membership. Opening the text box…"); setTimeout(() => nav(`/my-journeys?explain=${encodeURIComponent(userWords(next).join(" "))}`), 1500); }
      else toTextBox(userWords(next));
    }
    setBusy("");
  };
  const use = () => { axios.delete(`${API}/api/audio-journeys/flow-draft/chat`, auth()).catch(() => {}); sessionStorage.setItem("soulumo_guided_plan", JSON.stringify(plan)); nav("/my-journeys?guided=1"); };
  const lines = (plan?.sections || []).flatMap((s) => s.lines.map((l) => l.text));

  return (
    <div className="mt-6 rounded-3xl p-5 space-y-3" style={{ background: "rgba(241,236,221,0.05)", border: `1px solid ${C.line}` }} data-testid="create-help-chat">
      <div className="flex justify-end">
        <button type="button" onClick={toggleMute} data-testid="create-help-mute" aria-label={muted ? "Turn voice on" : "Mute voice"} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs" style={{ border: `1px solid ${C.line}`, color: C.gold }}>
          {muted ? <VolumeX size={13} /> : <Volume2 size={13} />} {muted ? "Voice off" : "Voice on"}
        </button>
      </div>
      {resume && (
        <div className="rounded-2xl p-4" style={{ background: "rgba(215,187,122,0.1)", border: `1px solid ${C.gold}` }} data-testid="create-help-resume">
          <p className="text-base" style={{ fontFamily: SERIF, color: C.cream }}>We were talking earlier. Shall we carry on where we left off?</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={carryOn} data-testid="create-help-resume-yes" className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: C.gold, color: C.bg }}>Carry on</button>
            <button type="button" onClick={startAgain} data-testid="create-help-resume-no" className="rounded-full px-4 py-2 text-xs" style={{ color: C.dim }}>Start again</button>
          </div>
        </div>
      )}
      {turns.map((t, i) => (
        <p key={i} className={t.role === "guide" ? "text-lg leading-snug" : "text-sm text-right"} style={{ fontFamily: t.role === "guide" ? SERIF : undefined, color: t.role === "guide" ? C.cream : C.gold }}
          data-testid={`create-help-turn-${i}`}>{t.text}</p>
      ))}
      {busy && <GuideWorking name={name} pronoun={who === "richard" ? "his" : "her"} kind={busy} />}
      {error && <p className="text-sm" style={{ color: "#e9a39c" }} data-testid="create-help-error">{error}</p>}
      {plan ? (
        <div data-testid="create-help-plan">
          <p className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: C.gold }}>{plan.journey_name || "Your affirmations"}</p>
          <ul className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {lines.map((l, i) => <li key={i} className="text-base animate-in fade-in slide-in-from-bottom-1 duration-500 fill-mode-both" style={{ fontFamily: SERIF, color: C.cream, animationDelay: `${Math.min(i, 24) * 140}ms` }} data-testid={`create-help-plan-line-${i}`}>{l}</li>)}
          </ul>
          <button type="button" onClick={use} data-testid="create-help-use" className="mt-4 w-full rounded-full py-3.5 text-xs uppercase tracking-[0.24em] font-bold inline-flex items-center justify-center gap-2" style={{ background: C.gold, color: C.bg }}>
            <Check size={15} /> Yes, these feel right
          </button>
          <button type="button" onClick={() => { setPlan(null); setTurns([...turns, { role: "guide", text: ADJUST }]); speakLine("adjust"); setHandsFree(true); }} data-testid="create-help-adjust" className="mt-2 block mx-auto text-sm underline" style={{ color: C.dim }}>Not quite. Let me add something</button>
        </div>
      ) : handsFree ? (
        micReady ? <HandsFree active={!busy && !speaking && !resume} onText={(t) => send(t)} onOff={() => setHandsFree(false)}
          onConfused={() => { setTurns((ts) => [...ts, { role: "guide", text: CONFUSED }]); setHandsFree(false); toTextBox(userWords(turns)); }} />
          : <MicCheck name={name} onReady={markMic} onType={() => setHandsFree(false)} gold={C.gold} cream={C.cream} />
      ) : !busy && (
        <>
          <ExplainInput value={text} onChange={setText} placeholder="Type or tap the mic and talk…" testId="create-help-input" rows={3} />
          <div className="flex gap-2">
            <button type="button" onClick={() => send()} disabled={text.trim().length < 2} data-testid="create-help-send" className="flex-1 rounded-full py-3 text-sm font-semibold disabled:opacity-40" style={{ background: C.gold, color: C.bg }}>Send</button>
            <button type="button" onClick={() => { unlockAudio(); setHandsFree(true); }} data-testid="create-help-handsfree-on" className="rounded-full px-4 py-3 text-sm inline-flex items-center gap-1.5" style={{ border: `1px solid ${C.gold}`, color: C.gold }}><Mic size={14} /> Hands-free</button>
            <button type="button" onClick={onEnd} data-testid="create-help-close" className="rounded-full px-4 py-3 text-sm" style={{ color: C.dim }}>Close</button>
          </div>
        </>
      )}
    </div>
  );
};

// Top card in the Soulumo guide: help creating an installation (AI for members; others go straight to Create).
export const CreateHelpCard = ({ onYes, first }) => (
  <button type="button" onClick={onYes} data-testid="create-help-card" className="mt-6 w-full rounded-3xl p-5 text-left flex items-center gap-4 transition-transform duration-200 hover:-translate-y-0.5"
    style={{ background: "var(--coach-card-gold)", border: `1px solid ${C.gold}` }}>
    <Sparkles size={22} style={{ color: C.gold }} className="shrink-0" />
    <span>
      <span className="block text-xl leading-tight" style={{ fontFamily: SERIF, color: C.cream }}>{first === null ? "Would you like help creating an installation?" : first ? "Would you like help with your first installation?" : "Would you like help with your installation?"}</span>
      <span className="block mt-1 text-xs" style={{ color: C.dim }}>Talk it through with me, and I'll write the affirmations.</span>
    </span>
  </button>
);
